// View-switching option builders for <vf-apy-chart>: the
// "position" (percentile-space history) and "mev" (per-epoch MEV
// component) alternates to the default "base" chart. Pure: data + theme
// in, option out. The coordinate system (x axis, grid, slider, tooltip
// chrome) is shared with the base view via chart-options.ts so a view
// switch changes the series, never the epoch domain or slider position;
// the percentile encoding lives in consistency.ts.
//
// Honesty rules enforced here:
// - Position view: percentiles on the shared piecewise-linear display
//   scale (consistency.ts: dynamic floor + p90 knee), documented by
//   labeled true-value reference lines and the footnote's scale sentence;
//   dots only, so a null percentile is simply absent — no bridging.
// - MEV view: component = delegator_apy_pct - delegator_apy_inflation_pct,
//   same epoch, ONLY where both are served (stats.ts mevComponentPp);
//   either side null -> null -> no bar, never a zero-height bar standing
//   in for absence. Bars, not a line: each epoch's MEV is a discrete
//   outcome, and a line would suggest continuity between lottery-shaped
//   events.
// - The MEV y axis starts at 0 (values are >= 0 in practice) but never
//   clamps: a negative component renders honestly below zero.
// - The MEV view carries no cluster overlay because the cluster MEV
//   distribution is not imported — MEV_VIEW_FOOTNOTE states that absence.

import type { BarSeriesOption, ScatterSeriesOption } from "echarts/charts";
import type {
  DataZoomComponentOption,
  GridComponentOption,
  MarkAreaComponentOption,
  MarkLineComponentOption,
  TooltipComponentOption,
} from "echarts/components";
import type { ComposeOption } from "echarts/core";

import {
  alpenglowMarkLine,
  fullChartGrid,
  fullChartSlider,
  fullChartTooltip,
  fullChartXAxis,
  mergeMarkLines,
  MONO_FONT,
  SANS_FONT,
  type ChartLayout,
  type ZoomWindow,
} from "./chart-options";
import {
  P90_REGION_NOTE,
  PERCENTILE_DEFINITION_NOTE,
  percentileScaleNote,
  percentileScatterSeries,
  percentileYAxis,
} from "./consistency";
import { formatPct, formatSignedPp, lamportsToSol } from "./format";
import type { Assembled } from "./series";
import { mevComponentPp } from "./stats";
import type { ThemeTokens } from "./theme";
import type { TooltipLine } from "./tooltip";

/**
 * Which plot renders below the answer band. "income" is the header
 * [validator income] toggle's view (income-view.ts), not a band tab.
 */
export type ChartView = "base" | "position" | "mev" | "income";

export type VfViewOption = ComposeOption<
  | ScatterSeriesOption
  | BarSeriesOption
  | GridComponentOption
  | TooltipComponentOption
  | DataZoomComponentOption
  | MarkAreaComponentOption
  | MarkLineComponentOption
>;

export const SERIES_MEV = "MEV component";

/**
 * Position-view footnote: the percentile derivation, the 90-100 region's
 * structural share, and the display-scale disclosure. `floor` is
 * percentileFloor over the same percentile array the view plots.
 */
export function positionViewFootnote(floor: number): string {
  return `${PERCENTILE_DEFINITION_NOTE} ${P90_REGION_NOTE} ${percentileScaleNote(floor)}`;
}

/** MEV-view footnote: derivation plus the factual absence of cluster context. */
export const MEV_VIEW_FOOTNOTE =
  "MEV component = net - base, same epoch, both measured; " +
  "cluster MEV distribution not yet imported, so no cluster overlay is shown.";

export interface ViewChartInputs {
  a: Assembled;
  theme: ThemeTokens;
  /** /income start_time for an epoch, for the x-axis secondary date row. */
  dateForEpoch: (epoch: number) => number | null;
  /** ECharts axis-trigger tooltip formatter (closes over per-epoch data). */
  tooltipFormatter: (params: unknown) => string;
  /** Shadow-DOM container the tooltip must render into (never document.body). */
  tooltipAppendTo: HTMLElement;
  /** Live dataZoom window carried across view switches; absent = full range. */
  zoom?: ZoomWindow;
  /** Container-width tier + pointer class from the collapse ladder; absent = the settled wide/fine desktop geometry. */
  layout?: ChartLayout;
}

/**
 * Position view: per-epoch our_base_percentile_pct dots on the shared
 * piecewise-linear display scale, over the same epoch spine and slider as
 * the base view. Series/reference-line encoding comes verbatim from
 * consistency.ts.
 */
export function buildPositionOption(i: ViewChartInputs): VfViewOption {
  const { a, theme: t } = i;
  const slider = fullChartSlider(a, t, i.zoom, i.layout);
  const positionSeries = percentileScatterSeries(a.ourBasePercentile, t);
  positionSeries.markLine = mergeMarkLines(positionSeries.markLine, alpenglowMarkLine(a.epochs, t));
  const option: VfViewOption = {
    animation: false,
    textStyle: { fontFamily: SANS_FONT },
    grid: fullChartGrid(slider !== undefined, i.layout, false),
    xAxis: fullChartXAxis(a, t, i.dateForEpoch),
    yAxis: percentileYAxis(t),
    tooltip: fullChartTooltip(t, i.tooltipFormatter, i.tooltipAppendTo),
    series: [positionSeries],
  };
  if (slider !== undefined) {
    option.dataZoom = slider;
  }
  return option;
}

/**
 * Per-epoch MEV component in pp: net - base, same epoch, both served;
 * either side null -> null (absence is never zero). Delegates to stats.ts
 * mevComponentPp — the same derivation the answer band's cell 5 uses.
 */
export function mevComponentSeries(
  our: readonly (number | null)[],
  ourBase: readonly (number | null)[],
): (number | null)[] {
  return our.map((v, idx) => mevComponentPp(v, ourBase[idx] ?? null));
}

/**
 * MEV view: per-epoch component bars (violet — the widget-wide net/MEV
 * ink), null epochs absent. boundaryGap true so edge bars never half-clip;
 * the epoch domain and slider window stay identical to the other views.
 */
export function buildMevOption(i: ViewChartInputs): VfViewOption {
  const { a, theme: t } = i;
  const slider = fullChartSlider(a, t, i.zoom, i.layout);
  const series: BarSeriesOption = {
    name: SERIES_MEV,
    type: "bar",
    data: mevComponentSeries(a.our, a.ourBase),
    itemStyle: { color: t.netLine },
    emphasis: { focus: "none" },
    barMaxWidth: 14,
    z: 3,
  };
  const option: VfViewOption = {
    animation: false,
    textStyle: { fontFamily: SANS_FONT },
    grid: fullChartGrid(slider !== undefined, i.layout, false),
    xAxis: fullChartXAxis(a, t, i.dateForEpoch, true),
    yAxis: {
      type: "value",
      // Starts at 0 without clamping: a negative component (never yet
      // observed) would extend the axis below zero and render honestly.
      min: (extent: { min: number; max: number }): number => Math.min(0, extent.min),
      axisLabel: { fontSize: 11, fontFamily: MONO_FONT, color: t.axisText, formatter: "{value} pp" },
      axisLine: { show: true, lineStyle: { color: t.axisLine } },
      splitLine: { show: true, lineStyle: { color: t.splitLine } },
    },
    tooltip: fullChartTooltip(t, i.tooltipFormatter, i.tooltipAppendTo),
    series: [series],
  };
  if (slider !== undefined) {
    option.dataZoom = slider;
  }
  return option;
}

/** One epoch's MEV tooltip facts, read from the raw byEpoch join (never fabricated). */
export interface EpochMevFacts {
  netPct: number | null;
  basePct: number | null;
  /** /income tips_gross_lamports: block-observed transfers to tip accounts. */
  tipsGrossLamports: number | null;
  /** /income mev_claimed_lamports: the validator's own MEV keep. */
  mevClaimedLamports: number | null;
}

/**
 * MEV-view tooltip rows: the derived component (written absence when
 * either input is missing — never 0), then its two inputs, then the
 * operator-lens SOL facts — what the epoch's blocks generated in tips and
 * what the validator itself kept. The pp rows answer the delegator's
 * question; the SOL rows answer the operator's ("how much does it make").
 * Violet swatches on the net-family rows, charcoal on base.
 */
export function mevTooltipLines(f: EpochMevFacts, t: ThemeTokens): TooltipLine[] {
  const mev = mevComponentPp(f.netPct, f.basePct);
  const rows: TooltipLine[] = [
    {
      label: "MEV component",
      value: mev === null ? "not derived (net or base not served)" : formatSignedPp(mev),
      color: t.netLine,
    },
    {
      label: "net incl MEV",
      value: f.netPct === null ? "not served" : formatPct(f.netPct),
      color: t.netLine,
    },
    {
      label: "base",
      value: f.basePct === null ? "not served" : formatPct(f.basePct),
      color: t.ourLine,
    },
  ];
  // Operator C54: absence is ONE informative line naming what is absent —
  // stacked bare "not served" rows read as "we make nothing", which is a
  // false impression, not a disclosure. A measured zero still renders as
  // the fact "0 SOL".
  if (f.tipsGrossLamports === null && f.mevClaimedLamports === null) {
    rows.push({ label: "no MEV settlement recorded for this epoch", value: "" });
  } else {
    rows.push(
      {
        label: "tips generated (block-observed)",
        value: f.tipsGrossLamports === null ? "not recorded" : `${lamportsToSol(f.tipsGrossLamports)} SOL`,
      },
      {
        label: "MEV claimed (validator keep)",
        value: f.mevClaimedLamports === null ? "not recorded" : `${lamportsToSol(f.mevClaimedLamports)} SOL`,
      },
    );
  }
  return rows;
}
